import { useEffect, useState } from "react";
import { Flex, Box, Text } from "@chakra-ui/react";
import inventoryService from "../../../service/inventory/inventory.service";
import { InventoryBulkFilter } from "../../../models/filter/inventory-bulk.filter";
import { InventoryModel } from "../../../models/inventory.model";
import {
  updateArchiveFilter,
  updatePageSize,
} from "../../../store/slice/Account/inventory.slice";
import { useAppDispatch, useAppSelector } from "../../../util/hooks.util";
import { downloadCsvToMachine } from "../../../util/file.util";
import { ButtonCustom } from "../../../components/form/controls/form.button";
import { InventoryTable } from "../../../components/app/inventory/inventory.table";
import { DownloadIcon } from "@chakra-ui/icons";
import { TableFooter } from "../../../components/shared/table/TableFooter";
import { Loading } from "../../../components/shared/Loading";
import { UploadInventoryModal } from "../../../components/app/inventory/upload-inventory.modal";
import { useSearchParams } from "react-router-dom";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";
import { formatCurrency } from "../../../util/number.util";

const HIDE_SOLD = false;

export const Archive = ({}) => {
  const dispatch = useAppDispatch();

  const { region } = useSelector((state: RootState) => state.userProfile);
  const {
    archiveFilter: filter,
    sortColumn,
    pageSize,
  } = useAppSelector((state) => state.inventory);

  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState({
    data: [],
    totalItems: 0,
    totalPage: 0,
  } as { data: InventoryModel[]; totalPage: number; totalItems: number });

  const [pageNum, setPageNum] = useState(1);
  const [checkedIds, setCheckedIds] = useState([] as string[]);
  const [showSold, setShowSold] = useState(false);
  const [searchParams] = useSearchParams();
  const [uploadOpen, setUploadOpen] = useState(false);

  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [activeInventory, setActiveInventory] = useState(null);
  const [searchParamReady, setSearchParamReady] = useState(false);
  const [metrics, setMetrics] = useState<{
    totalWeight: number;
    totalSalePrice: number;
    totalRebate: number;
  } | null>(null);

  useEffect(() => {
    if (searchParams.get("inventory_id")) {
      setActiveInventory(searchParams.get("inventory_id"));
    }
    if (searchParams.get("searchTerm")) {
      setGlobalSearchTerm(searchParams.get("searchTerm"));
    }

    setSearchParamReady(true);
  }, [searchParams]);

  const init = async (page?: number) => {
    setLoading(true);
    const filterPayload = filter
      ? { ...filter, status: "SOLD", searchTerm: globalSearchTerm }
      : { status: "SOLD", searchTerm: globalSearchTerm };

    const itemsPrms = new Promise((resolve, reject) => {
      inventoryService
        .getList(
          {
            ...filterPayload,
            ids: activeInventory ? [activeInventory] : undefined,
          },
          HIDE_SOLD,
          page ?? pageNum,
          pageSize,
          sortColumn,
          region
        )
        .then((inventory) => {
          resolve(inventory);
        })
        .catch(reject);
    });

    const metricsPrms = new Promise((resolve, reject) => {
      inventoryService
        .getMetrics(filterPayload, region)
        .then((metrics) => {
          resolve(metrics);
        })
        .catch(reject);
    });

    await Promise.all([itemsPrms, metricsPrms])
      .then(([inventory, metrics]) => {
        setInventory(
          inventory as {
            data: InventoryModel[];
            totalPage: number;
            totalItems: number;
          }
        );
        setMetrics(
          metrics as {
            totalWeight: number;
            totalSalePrice: number;
            totalRebate: number;
          }
        );
      })
      .catch((error) => {
        console.error("Error fetching inventory:", error);
        setInventory({ data: [], totalPage: 0, totalItems: 0 });
        setMetrics(null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleExport = async () => {
    setLoading(true);
    await inventoryService
      .exportToCsv({
        ids: !!checkedIds?.length ? checkedIds : undefined,
        ...filter,
      })
      .then((csvData) => {
        downloadCsvToMachine(csvData, "inventory-export.csv");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (searchParamReady) {
      init();
    }
  }, [globalSearchTerm, searchParamReady, filter, sortColumn, pageSize]);

  return (
    <>
      <Loading loading={loading} ignoreDelay />
      <Flex
        flexDir="row"
        justifyContent="flex-end"
        alignItems="center"
        gap="8px"
        mb="8px"
      >
        <Flex flexDir="row" gap="6px" justifyContent="flex-end">
          <ButtonCustom
            disabled={!inventory.data?.length}
            onClick={handleExport}
          >
            Export
            <DownloadIcon ml="6px" />
          </ButtonCustom>
        </Flex>
      </Flex>

      {/* Weight Metrics Card */}
      {metrics && (
        <Flex
          flexDirection="row"
          gap={4}
          mb={4}
          p={4}
          bg="white"
          borderRadius="md"
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.200"
        >
          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Total Weight
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="brand.green">
              {metrics.totalWeight || 0} kg
            </Text>
          </Box>
          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Total Sale
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="brand.green">
              ${formatCurrency(metrics.totalSalePrice || 0)}
            </Text>
          </Box>
          <Box flex={1}>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Total Rebate
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="brand.green">
              ${formatCurrency(metrics.totalRebate || 0)}
            </Text>
          </Box>
        </Flex>
      )}

      {!loading && (
        <InventoryTable
          key={inventory.data.length}
          excludeColumns={[
            "zolo_custom_bin_no.",
            "quotePrice",
            "quotePriceMax",
          ]}
          inventory={inventory.data}
          onDelete={(id: string) =>
            setInventory({
              data: inventory.data.filter((i) => i.id !== id),
              totalPage: inventory.totalPage,
              totalItems: inventory.totalItems,
            })
          }
          onEdit={init}
          onCheckChange={(data) =>
            setCheckedIds(data.filter((x) => x.isChecked).map((x) => x.id))
          }
          onFilterChange={(filter: InventoryBulkFilter) => {
            setPageNum(1);
            dispatch(updateArchiveFilter(filter));
          }}
          onSort={() => setPageNum(1)}
          footer={
            <TableFooter
              count={inventory.data?.length}
              totalItems={inventory.totalItems}
              totalPage={inventory.totalPage}
              pageNum={pageNum}
              onChange={(num) => {
                setPageNum(num);
                init(num);
              }}
              pageSize={pageSize}
              onPageSizeChange={(size) => {
                setPageNum(1);
                dispatch(updatePageSize({ pageSize: size }));
              }}
            />
          }
        />
      )}

      <UploadInventoryModal
        isBulkEdit
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSubmit={() => {
          setPageNum(1);
          init(1);
        }}
      />
    </>
  );
};
